import { useMemo, useRef, useState } from "react";
import { X, Send, FileText, Download, Copy, RotateCcw, Check } from "lucide-react";
import {
  useCatalog,
  useEndpointFixtures,
  useTestExecution
} from "./hooks";
import { downloadEndpointFile } from "../../../services/testExecutionService";
import {
  EndpointBrowser,
  LoadingSpinner,
  ErrorMessage,
  EmptyState
} from "./EndpointList";
import { ApiResponseDisplay, ParametersSection } from "./ResponseDisplay";
import {
  buildFieldSpecs,
  validateParameters,
  buildRequestPreview,
  buildCurl,
  responseFormat
} from "./utils";

/** A production base URL means real, billable calls — say so, loudly. */
const isSandbox = (baseUrl = "") =>
  /sandbox|staging|\.test\.|localhost|127\.0\.0\.1|uat/i.test(baseUrl);

const REQUEST_TABS = [
  { id: "params", label: "Parameters" },
  { id: "body", label: "JSON body" },
  { id: "curl", label: "cURL" }
];

export default function TestApiModal({
  isOpen,
  onClose,
  service,
  integration
}) {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("params");
  const [copied, setCopied] = useState(false);
  const fieldRefs = useRef({});

  const {
    endpoints,
    apiMeta,
    loading: loadingCatalog,
    error: catalogError
  } = useCatalog(isOpen, service);
  const {
    parameters,
    currentFixtureId,
    loading: loadingFixtures,
    fetchFixtures,
    updateParameter,
    resetFixtures
  } = useEndpointFixtures();
  const {
    response,
    loading: loadingTest,
    error: testError,
    executeApiTest,
    clearResponse
  } = useTestExecution();

  // Derive controls, required-ness and limits from the endpoint's own schema.
  const specs = useMemo(
    () => buildFieldSpecs(selectedEndpoint?.request_schema, parameters),
    [selectedEndpoint, parameters]
  );
  const problems = useMemo(
    () => validateParameters(specs, parameters),
    [specs, parameters]
  );

  if (!isOpen) return null;

  const baseUrl = apiMeta?.base_url || "";
  const sandbox = isSandbox(baseUrl);

  const handleEndpointClick = async (endpoint) => {
    setSelectedEndpoint(endpoint);
    clearResponse();
    setDownloadError(null);
    setTab("params");
    resetFixtures();
    await fetchFixtures(endpoint);
  };

  // Coerce numeric inputs before they reach state. Booleans and arrays already
  // arrive correctly typed from their own controls.
  const handleParameterChange = (key, value) => {
    const spec = specs.find((s) => s.key === key);
    if (spec?.control === "number" && typeof value === "string" && value !== "") {
      const n = Number(value);
      updateParameter(key, Number.isNaN(n) ? value : n);
      return;
    }
    updateParameter(key, value);
  };

  const focusField = (key) => {
    setTab("params");
    requestAnimationFrame(() => {
      const el = fieldRefs.current[key];
      if (el) {
        el.focus();
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    });
  };

  const handleSendRequest = async () => {
    if (!selectedEndpoint || problems.length) return;
    try {
      await executeApiTest(selectedEndpoint, parameters, currentFixtureId);
    } catch (err) {
      // Surfaced by the hook.
    }
  };

  const handleCopyCurl = async () => {
    try {
      await navigator.clipboard.writeText(
        buildCurl({ baseUrl, endpoint: selectedEndpoint, parameters })
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleDownload = async () => {
    if (!selectedEndpoint) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await downloadEndpointFile(
        selectedEndpoint.endpoint_id,
        parameters
      );
      let filename = response?.data?.filename || "download";
      const cd = res.headers?.["content-disposition"];
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        if (m) {
          try {
            filename = decodeURIComponent(m[1]);
          } catch {
            filename = m[1];
          }
        }
      }
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      setDownloadError("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const error = catalogError || testError;
  const preview = buildRequestPreview(parameters);
  const fmt = selectedEndpoint ? responseFormat(selectedEndpoint) : "JSON";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center !mt-0">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[88vh] flex flex-col mx-4">
        {/* ── Header */}
        <div className="flex items-center gap-3 px-6 py-3.5 border-b border-gray-200">
          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
            {integration.iconPath ? (
              <img
                src={integration.iconPath}
                alt={integration.name}
                className="w-5 h-5"
              />
            ) : (
              <span className="text-blue-600 font-bold">
                {integration.name?.[0] || "?"}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate">
              {service.name}
            </h2>
            <p className="text-xs text-gray-500">Test API endpoints</p>
          </div>

          <div className="flex-1" />

          {baseUrl && (
            <>
              <span
                className={`text-[11px] font-bold px-2 py-1 rounded-full border ${
                  sandbox
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-red-50 text-red-700 border-red-200"
                }`}
              >
                {sandbox ? "SANDBOX" : "PRODUCTION — BILLABLE"}
              </span>
              <span
                className="hidden lg:inline font-mono text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1 max-w-xs truncate"
                title={baseUrl}
              >
                {baseUrl.replace(/^https?:\/\//, "")}
              </span>
            </>
          )}

          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Body: endpoints | request | response */}
        <div className="flex flex-1 overflow-hidden">
          {/* Endpoints */}
          <div className="w-[290px] border-r border-gray-200 bg-gray-50 p-3 overflow-y-auto shrink-0">
            <div className="flex items-baseline gap-2 mb-2 px-1">
              <h3 className="text-sm font-semibold text-gray-700">Endpoints</h3>
              {!loadingCatalog && !catalogError && (
                <span className="text-xs text-gray-400">
                  {endpoints.length}
                </span>
              )}
            </div>
            {loadingCatalog ? (
              <LoadingSpinner message="Loading endpoints..." />
            ) : catalogError ? (
              <ErrorMessage message={catalogError} />
            ) : endpoints.length > 0 ? (
              <EndpointBrowser
                endpoints={endpoints}
                query={query}
                onQueryChange={setQuery}
                selectedId={selectedEndpoint?.endpoint_id}
                onSelect={handleEndpointClick}
              />
            ) : (
              <EmptyState message="No endpoints available" />
            )}
          </div>

          {!selectedEndpoint ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <FileText className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">Select an endpoint to test</p>
              <p className="text-sm text-gray-400 mt-1">
                Choose from the endpoints list on the left
              </p>
            </div>
          ) : (
            <>
              {/* Request */}
              <div className="flex-1 min-w-0 flex flex-col border-r border-gray-200">
                <div className="px-5 pt-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                      {selectedEndpoint.method}
                    </span>
                    <h3 className="text-base font-mono font-semibold text-gray-900 truncate">
                      {selectedEndpoint.path}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    {selectedEndpoint.description}
                  </p>
                </div>

                <div className="flex gap-1 px-5 mt-3 border-b border-gray-200">
                  {REQUEST_TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        tab === t.id
                          ? "text-blue-700 border-blue-600"
                          : "text-gray-500 border-transparent hover:text-gray-700"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto px-5 pt-4">
                  {tab === "params" && (
                    <ParametersSection
                      specs={specs}
                      parameters={parameters}
                      problems={problems}
                      loadingFixtures={loadingFixtures}
                      onParameterChange={handleParameterChange}
                      fieldRefs={fieldRefs}
                    />
                  )}
                  {tab === "body" && (
                    <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-xs font-mono overflow-x-auto mb-6">
                      {JSON.stringify(preview.body, null, 2)}
                    </pre>
                  )}
                  {tab === "curl" && (
                    <pre className="bg-gray-900 text-gray-300 rounded-lg p-4 text-xs font-mono overflow-x-auto mb-6 whitespace-pre">
                      {buildCurl({
                        baseUrl,
                        endpoint: selectedEndpoint,
                        parameters
                      })}
                    </pre>
                  )}
                </div>

                <div className="px-5 py-3 border-t border-gray-200 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleSendRequest}
                    disabled={loadingTest || loadingFixtures || problems.length > 0}
                    className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    {loadingTest ? "Sending..." : "Send Test Request"}
                  </button>
                  <button
                    onClick={handleCopyCurl}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    {copied ? "Copied" : "Copy as cURL"}
                  </button>
                  <button
                    onClick={() => handleEndpointClick(selectedEndpoint)}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </button>
                  {problems.length > 0 && (
                    <span className="text-xs text-red-600">
                      {problems.length === 1
                        ? "1 field needs attention"
                        : `${problems.length} fields need attention`}
                    </span>
                  )}
                </div>
              </div>

              {/* Response */}
              <div className="w-[42%] min-w-[340px] flex flex-col shrink-0">
                <div className="px-5 py-2.5 border-b border-gray-200 flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Response
                  </h4>
                  {fmt !== "JSON" && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">
                      {fmt}
                    </span>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  {!response && !error && (
                    <p className="text-sm text-gray-500">
                      Press <b>Send Test Request</b> to call this endpoint.
                    </p>
                  )}

                  {response && (
                    <>
                      <ApiResponseDisplay
                        response={response}
                        specs={specs}
                        onFocusField={focusField}
                      />

                      {/* Binary response (e.g. a .zip) — offer a real download */}
                      {response?.data?._binary && (
                        <div className="mt-4">
                          <button
                            onClick={handleDownload}
                            disabled={downloading}
                            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Download className="w-4 h-4" />
                            {downloading
                              ? "Downloading…"
                              : `Download ${response.data.filename || "file"}`}
                          </button>
                          {response.data.content_type && (
                            <p className="mt-2 text-xs text-gray-500">
                              {response.data.content_type}
                              {response.data.content_length
                                ? ` · ${Math.round(
                                    response.data.content_length / 1024
                                  )} KB`
                                : ""}
                            </p>
                          )}
                          {downloadError && (
                            <p className="mt-2 text-sm text-red-600">
                              {downloadError}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
